// externals
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { CommonConsumer, IShopper } from '@makemydeal/dr-platform-types';

// types
import type { MandatoryDetailsValues } from './MandatoryDetailsCard';

export interface UseMandatoryDetailsSubmitArgs {
    onComplete?: () => void;
}

export interface UseMandatoryDetailsSubmitResult {
    initialValues: Partial<MandatoryDetailsValues>;
    onSubmit: (values: MandatoryDetailsValues) => void;
    isSubmitting: boolean;
}

// Carry the buyer's existing contact fields through so the consumer-card merge in
// `combineCommonConsumerContactInfo` doesn't blank out fields we don't capture in this form
// (email, phone, etc.). In optional-desking mode that merge spreads "required" fields even
// when the value is undefined — omitting them here would wipe them from offer state and the
// auto-save-on-refresh (PR #9113) would persist that partial state, breaking the deal.
const buildCustomerPayload = (
    values: MandatoryDetailsValues,
    consumerId: string,
    dealershipId: string,
    buyer: IShopper
): CommonConsumer => ({
    consumerId,
    dealershipId,
    dealerGroupId: '',
    firstName: buyer.firstName ?? '',
    middleName: buyer.middleName,
    lastName: buyer.lastName ?? '',
    title: buyer.title,
    suffix: buyer.suffix,
    primaryEmail: buyer.email,
    alternateEmail: buyer.emailAlternative,
    cellPhone: buyer.cellPhone,
    homePhone: buyer.homePhone,
    workPhone: buyer.workPhone,
    business: buyer.business,
    billingAddress: buyer.billingAddress,
    countyOrRegion: buyer.county,
    street1: values.streetAddress,
    street2: values.streetAddress2,
    city: values.city,
    stateOrProvinceAbbreviation: values.state,
    postalCode: values.postalCode,
    dateOfBirth: values.dateOfBirth
});

/**
 * Hook used by State 1 (MandatoryDetailsCard) consumers to capture DOB + full address,
 * save them through the existing "Apply" customer-card path, and then trigger the
 * soft-pull consent flow so AI recommendations refresh with verified credit data.
 *
 * Returns pre-populated `initialValues` from the current shopper + customer SPD record,
 * an `onSubmit` callback for the card, and `isSubmitting` derived from the customer SPD
 * save loading state.
 *
 * @example
 * ```tsx
 * const { initialValues, onSubmit, isSubmitting } = useMandatoryDetailsSubmit({
 *     onComplete: () => onClose?.()
 * });
 *
 * return (
 *     <MandatoryDetailsCard initialValues={initialValues} onSubmit={onSubmit} isSubmitting={isSubmitting} />
 * );
 * ```
 */
export const useMandatoryDetailsSubmit = ({ onComplete }: UseMandatoryDetailsSubmitArgs = {}): UseMandatoryDetailsSubmitResult => {
    const dispatch = useDispatch();

    const buyer = useSelector(offerReduxSelectors.getShopperInfo);
    const consumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const dealershipId = useSelector(offerReduxSelectors.getCommonOrgId);
    const isOptionalDesking = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isSavingSpd = useSelector(customerSpdSelectors.getSaveCustomerSpdLoading());
    const isFetchingSpd = useSelector(customerSpdSelectors.getCustomerSpdLoading());
    const customerSpdData = useSelector((state: StateTree) => customerSpdSelectors.getCustomerSpdData(state, consumerId));
    const checkSpdData = useSelector(customerSpdSelectors.getCheckCustomerSpdResult(consumerId));
    const isCustomerSpdLoaded = useSelector((state: StateTree) =>
        consumerId ? customerSpdSelectors.getCustomerAPISuccess(state, consumerId) : false
    );

    // Fetch customer SPD data on mount so DOB can pre-populate from BFF.
    // CustomerCard normally gates this behind the user clicking "Show Data"; here we
    // load eagerly because the AC requires "pre-populate the fields with the customer details"
    // and the user is already committing to entering DOB on this form.
    useEffect(() => {
        if (consumerId && !isCustomerSpdLoaded) {
            dispatch(customerSpdActionCreators.customerSpdRSAA(consumerId));
        }
    }, [consumerId, isCustomerSpdLoaded, dispatch]);

    const initialValues = useMemo<Partial<MandatoryDetailsValues>>(
        () => ({
            streetAddress: buyer.streetAddress ?? '',
            streetAddress2: buyer.streetAddress2 ?? '',
            city: buyer.city ?? '',
            state: buyer.state ?? '',
            postalCode: buyer.zip ?? '',
            dateOfBirth: customerSpdData?.dateOfBirth ?? ''
        }),
        [buyer.streetAddress, buyer.streetAddress2, buyer.city, buyer.state, buyer.zip, customerSpdData?.dateOfBirth]
    );

    const onSubmit = useCallback(
        (values: MandatoryDetailsValues) => {
            const customerPayload = buildCustomerPayload(values, consumerId, dealershipId, buyer);

            // Persist address fields to offer state (mirrors CustomerCard's applyCustomerChanges path).
            dispatch(offerReduxActionCreators.updatedShopperLocationFromConsumer(buyer, customerPayload, false, isOptionalDesking));

            // Persist DOB to customer SPD slice and mark the customer record as needing sync.
            // Guards:
            //  - `checkSpdData` must exist — the reducer Object.keys-es it; missing data throws.
            //  - `customerSpdData` must exist — the SPD reducer REPLACES the whole record with
            //    whatever we pass. If we dispatch without prior SPD data loaded, the spread of
            //    undefined collapses to `{dateOfBirth}` only, wiping SSN/DL/etc. client-side and
            //    persisting the empty record on the next Save.
            if (consumerId) {
                dispatch(editCustomerActionCreators.setCustomerToSync(consumerId));
                if (checkSpdData && customerSpdData) {
                    const updatedSpdData = {
                        ...customerSpdData,
                        dateOfBirth: values.dateOfBirth
                    };
                    dispatch(customerSpdActionCreators.updateCustomerSpd(consumerId, updatedSpdData));
                }
            }

            // Trigger the existing soft-pull flow — consent flag tells middleware to refresh AI with verified credit.
            dispatch(offerActionCreators.setSoftPullConsentProvided(true));

            onComplete?.();
        },
        [dispatch, buyer, consumerId, dealershipId, isOptionalDesking, checkSpdData, customerSpdData, onComplete]
    );

    // Block the submit button while SPD is fetching too — without the fetched record we'd
    // wipe SSN/DL on dispatch (the SPD reducer replaces the whole record), so disabling
    // submit is safer than letting the dispatch silently no-op.
    const isSubmitting = isSavingSpd || isFetchingSpd;

    return { initialValues, onSubmit, isSubmitting };
};
